import * as Sentry from '@sentry/react';

/**
 * Initialize Sentry (only if DSN is provided)
 */
export function initSentry() {
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  
  if (!dsn) {
    console.warn('Sentry DSN not provided. Error tracking disabled.');
    return;
  }
  
  Sentry.init({
    dsn,
    environment: import.meta.env.MODE,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],
    tracesSampleRate: 1.0,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    beforeSend(event, hint) {
      // Don't send errors in development
      if (import.meta.env.DEV) {
        console.log('[Sentry] Error captured (dev mode, not sending):', event);
        return null;
      }
      return event;
    },
  });
  
  console.log('[Sentry] Initialized successfully');
}

/**
 * Error capture helper
 */
export function captureError(error: unknown, context?: {
  extra?: Record<string, unknown>;
  tags?: Record<string, string>;
}) {
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  
  if (!dsn) {
    console.log('[Sentry] Error captured (logging only):', error);
    return;
  }
  
  Sentry.captureException(error, {
    extra: context?.extra,
    tags: context?.tags,
  });
}

/**
 * Capture message (for logging non-error events)
 */
export function captureMessage(message: string, level?: 'info' | 'warning' | 'error') {
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  
  if (!dsn) {
    console.log(`[Sentry] Message [${level || 'info'}]:`, message);
    return;
  }
  
  Sentry.captureMessage(message, level);
}

/**
 * Set user context for error tracking
 */
export function setUserContext(user: {
  id: string;
  email?: string;
  username?: string;
}) {
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  
  if (!dsn) {
    return;
  }
  
  Sentry.setUser(user);
}

/**
 * Clear user context (on logout)
 */
export function clearUserContext() {
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  
  if (!dsn) {
    return;
  }
  
  Sentry.setUser(null);
}
